import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { assetKindOf, type AssetKind } from 'fym-shared/src/music24/assetKinds';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useGetReleaseKitQuery } from '../../../redux/api/fymAPI';
import { KIT_ASSET_WORKING_STATUSES } from '../../../redux/api/fymAPI.types';
import { useAccountId } from '../../../redux/slices/appInfo/hooks';
import { filesRedirectTarget, linkedFileUuid } from './filesRedirectTarget';

const REEL_BUSY: ReadonlyArray<string> = ['queued', 'analysing', 'planning', 'rendering', 'failed'];

/**
 * The old Assets route (`promote/promoteAssets`). Posts replaced Assets in the menu (design
 * assets-rethink d1, owner 2026-10-09): a saved link opens the file in its home by its kind
 * (`filesRedirectTarget`). A uuid in the link is looked up with `assets.locate`; a song with
 * `state=` reads the release kit to see which kind is made now or failed. The Work route is a page
 * again (owner, 2026-10-11): `work/WorkLinkRedirect.tsx` and `work/workLinkTarget.ts`.
 */
const PromoteFilesRedirectComponent: React.FC = () => {
  const { search } = useLocation();
  const accountId = useAccountId();
  const trpcAPI = useTRPCAPI();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const uuid = linkedFileUuid(search);
  const song = params.get('catalogItemUuid') ?? '';
  const wantsKinds = uuid === '' && song !== '' && params.get('state') !== null;

  const locate = useTRPCQuery(
    trpcAPI.assets.locate.queryOptions(
      { accountId: accountId ?? 0, uuid },
      { enabled: accountId !== null && uuid !== '', retry: false, staleTime: Infinity }
    )
  );
  const release = useGetReleaseKitQuery(
    { accountId: accountId ?? 0, catalogItemUuid: song },
    { skip: accountId === null || !wantsKinds }
  );
  const busyKinds = useMemo(() => {
    if (release.data === undefined) {
      return undefined;
    }
    const kinds = new Set<AssetKind>();
    (release.data.newest ?? release.data.assets)
      .filter((asset) => asset.status === 'failed' || KIT_ASSET_WORKING_STATUSES.includes(asset.status))
      .forEach((asset) => kinds.add(assetKindOf(asset.themeId)));
    if (release.data.reels.some((reel) => REEL_BUSY.includes(reel.status))) {
      kinds.add('post');
    }
    return kinds;
  }, [release.data]);

  const waitsForLocate = uuid !== '' && accountId !== null && locate.isLoading;
  const waitsForKinds = wantsKinds && accountId !== null && release.isLoading;
  if (accountId === null || waitsForLocate || waitsForKinds) {
    return <LoadingView />;
  }
  const located = uuid === '' ? undefined : (locate.data ?? null);
  return <Navigate to={filesRedirectTarget({ search, located, busyKinds })} replace />;
};

export const PromoteFilesRedirect = TypedMemo(PromoteFilesRedirectComponent);
